import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { WithPluginState } from '@atlaskit/editor-common/with-plugin-state';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { useAIThemeColor } from '@atlassian/generative-ai-modal/styles/theme';
import { AtlassianIntelligenceToolbarButton } from '@atlassian/generative-ai-modal/components/AtlassianIntelligenceToolbarButton';
import { Global } from '@emotion/react';
import { mapConfigItemsToQuickInsertItems } from './config-items/map-config-items-to-quick-insert-items';
import { createGetEditorViewPlugin } from './pm-plugins/get-editor-view/get-editor-view-plugin';
import { createDecorationPlugin } from './pm-plugins/decoration/decoration-plugin';
import { aiExperienceDecorationPluginKey } from './pm-plugins/decoration/decoration-plugin-key';
import { createEndAIExperienceCommand, createOpenAIModalCommand } from './pm-plugins/decoration/actions';
import { toggleSpellingAndGrammar } from './pm-plugins/ai-spelling-grammar/commands';
import { createPatchAnnotationFloatingToolbarPlugin } from './pm-plugins/patch-annotation-floating-toolbar/patch-annotation-floating-toobar-plugin';
import { createAIButtonPlugin } from './pm-plugins/ai-button/ai-button-plugin';
import { aiButtonPluginKey } from './pm-plugins/ai-button/ai-button-plugin-key';
import { ExperienceApplication } from './experience-application/ExperienceApplication';
import { ModalRegion } from './ui/modal/modal';
import { ModalRegionErrorBoundary } from './ui/components/ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { isExperimentEnabled, getExperimentCohort } from '@atlassian/generative-ai-modal/utils/experiments';
import { getPluginState } from './pm-plugins/decoration/decoration-plugin-factory';
import { globalStyles } from './ui/modal/styles';
import { hasGeneratedContentDecorations } from './pm-plugins/decoration/utils/hasGeneratedContentDecorations';
import { SafePlugin } from './pm-plugins/local-safe-plugin';
import { aiActorCache } from './ai-actor-cache';
import { getAIExperienceButton, getAIExperienceDropdowns } from './pm-plugins/floating-toolbar-button/floating-toolbar-button';
import { createSelectionPreviewPlugin } from './pm-plugins/selection-preview/selection-preview-plugin';
import { createAIPlaceholderPlugin } from './pm-plugins/ai-placeholder/ai-placeholder-plugin';
import { createAISpellingGrammarPlugin } from './pm-plugins/ai-spelling-grammar/ai-spelling-grammar-plugin';
import { createAskAIButtonTrackerPlugin } from './pm-plugins/floating-ai-button-plugin/floating-ai-button-tracker-plugin';
import { floatingAiButtonTrackerPluginKey } from './pm-plugins/floating-ai-button-plugin/floating-ai-button-tracker-plugin-key';
import { AskAIButtonWrapper } from './ui/components/AskAIButton/ask-ai-button-wrapper';
import { createAISuggestTitleFromTitleToolbarPlugin } from './pm-plugins/ai-suggest-title-from-title-toolbar/ai-suggest-title-from-title-toolbar-plugin';
import { IconButton } from '@atlaskit/button/new';
import { summarisePage, triggerAIPanelFeedbackCollector, convertExtension, regenerateExtension, findActionItems, getAIPanelContext } from './actions/actions';
import { aiSpellingGrammarPluginKey } from './pm-plugins/ai-spelling-grammar/ai-spelling-grammar-plugin-key';
import { getFloatingToolbarConfig } from './toolbar';
import { createAIPanelsPlugin } from './pm-plugins/ai-panels/ai-panels-plugin';
import { isAIPanelsEnabled } from './extensions/ai-panels/utils';
const GlobalWrapper = ({
  children
}) => {
  const aiThemeColor = useAIThemeColor();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Global, {
    styles: globalStyles(aiThemeColor)
  }), children);
};

/**
 * This is an {@link EditorPlugin}.
 * [Editor Plugins](https://product-fabric.atlassian.net/wiki/spaces/E/pages/56115413/New+Editor+Architecture#NewEditorArchitecture-Implementation)
 * are the mechanism for extending the editor with additional functionality such as nodes, marks, UI components etc.
 *
 * This EditorPlugin is responsible for adding such functionality for the generative ai experiences in the Editor.
 *
 * It is setup with an {@link EditorPluginAIProvider} which provides the configuration for the plugin including;
 * - the api to use for generating responses to prompts
 * - and the list of prompts that should be available in the editor grouped by category (empty, range)
 * @see {@link EditorPluginAIProvider} for more details.
 */
export class EditorPluginAI {
  // Facade EditorPlugin methods

  constructor({
    editorPluginAIProvider,
    aiGlobalOptIn
  }) {
    _defineProperty(this, "name", 'aiExperience');
    this.editorPluginAIProvider = editorPluginAIProvider;
    if (aiGlobalOptIn.status === 'disabled') {
      return;
    }
    this.nextPlugin = NextEditorPluginAI({
      config: {
        editorPluginAIProvider,
        aiGlobalOptIn
      }
    });
    this.pluginsOptions = this.nextPlugin.pluginsOptions;
    this.pmPlugins = this.nextPlugin.pmPlugins;
    this.primaryToolbarComponent = this.nextPlugin.primaryToolbarComponent;
    this.contentComponent = this.nextPlugin.contentComponent;
  }
}

/**
 * This is exported out to unblock the eventual deprecation of
 * `dangerouslyAppendPlugins`, when plugins can be supplied/added to presets in
 * EditorNext - there should be no functional differences between a
 * `NextEditorPluginAI` & `EditorPluginAI`.
 */
export const NextEditorPluginAI = ({
  config: {
    editorPluginAIProvider,
    aiGlobalOptIn
  },
  api
}) => {
  var _editorPluginAIProvid, _editorPluginAIProvid2;
  let editorView;
  const setEditorView = newEditorView => {
    editorView = newEditorView;
  };
  let providerFactory;
  const setProviderFactory = newProviderFactory => {
    providerFactory = newProviderFactory;
  };
  let newIDMap = {};
  const updateIdMap = ({
    idMap,
    selectionType
  }) => {
    newIDMap = idMap;
  };

  // TODO: clean up once this version is implemented in confluence and aiGlobalOptIn is made mandatory
  const aiOptIn = aiGlobalOptIn || {
    status: 'enabled',
    triggerOptInFlow: () => {}
  };
  const isFullPageExperimentsEnabled = !!editorPluginAIProvider.isFullPageExperimentsEnabled;
  const isRealtimeSGEnabled = !!getBooleanFF('platform.editor.ai-real-time-spelling-and-grammar_mtaik');
  const isProactiveAISupported = Boolean(isRealtimeSGEnabled && !!((_editorPluginAIProvid = editorPluginAIProvider.proactiveAIConfig) !== null && _editorPluginAIProvid !== void 0 && _editorPluginAIProvid.apiUrl) && ((_editorPluginAIProvid2 = editorPluginAIProvider.proactiveAIConfig) === null || _editorPluginAIProvid2 === void 0 ? void 0 : _editorPluginAIProvid2.enabled));
  const quickInsert = intl => {
    const configItems = [];
    const {
      baseGenerate,
      getSuggestions,
      disableQuickInsert
    } = editorPluginAIProvider.emptyConfig;
    if (!disableQuickInsert) {
      configItems.push(baseGenerate, ...getSuggestions({}));
    }
    const quickInsertItems = mapConfigItemsToQuickInsertItems({
      configItems,
      formatMessage: intl.formatMessage,
      aiGlobalOptIn: aiOptIn,
      // The editor view will always be available when quick insert items are being created
      editorView: editorView
    });
    return quickInsertItems;
  };
  if (getBooleanFF('platform.editor.ai-dynamic-show-hide-quick-insert-items_4ie8s')) {
    // Ignore to allow current editor-plugin-ai version to be adopted
    // without causing type issues if the corresponding editor-common version
    // isn't adopted at the same time.
    // @ts-ignore-next-line
    quickInsert.disableMemo = true;
  }
  return {
    name: 'aiExperience',
    pmPlugins: () => {
      const plugins = [{
        name: 'ai-experience-actor',
        plugin: () => new SafePlugin({
          view: editorView => {
            // This sets up an [actor](https://xstate.js.org/docs/guides/actors.html#actor-api) using
            // the editor-plugin-ai machine and starts it up.
            const editorPluginAIActor = aiActorCache.getActor(editorView);
            return {
              destroy: () => {
                // This cleansup any listeners from the actor -- to ensure we don't leak memory.
                editorPluginAIActor === null || editorPluginAIActor === void 0 ? void 0 : editorPluginAIActor.stop();
                aiActorCache.delete(editorView);
              }
            };
          }
        })
      }, {
        name: 'aiExperience',
        plugin: options => createDecorationPlugin({
          dispatchAnalyticsEvent: options.dispatchAnalyticsEvent,
          dispatch: options.dispatch,
          api
        })
      }, {
        name: 'ai-experience-get-editor-view-and-provider-factory',
        plugin: options => {
          setProviderFactory(options.providerFactory);
          return createGetEditorViewPlugin({
            setEditorView: setEditorView
          });
        }
      }, {
        name: 'ai-experience-ai-button',
        plugin: options => createAIButtonPlugin({
          dispatch: options.dispatch
        })
      }, {
        name: 'ai-experience-selection-preview',
        plugin: () => createSelectionPreviewPlugin()
      }, {
        name: 'ai-experience-floating-ai-button-tracker',
        plugin: options => createAskAIButtonTrackerPlugin({
          dispatch: options.dispatch
        })
      }];
      if (isProactiveAISupported) {
        plugins.push({
          name: 'ai-spelling-grammar-plugin',
          plugin: ({
            dispatch,
            getIntl
          }) => {
            return createAISpellingGrammarPlugin({
              dispatch,
              getIntl,
              proactiveAIConfig: editorPluginAIProvider.proactiveAIConfig
            });
          }
        });
      }
      if (!getBooleanFF('platform.editor.enable-selection-toolbar_ucdwd')) {
        plugins.push({
          name: 'ai-patch-annotation-floating-toolbar',
          plugin: options => createPatchAnnotationFloatingToolbarPlugin({
            getIntl: options.getIntl,
            rangeConfig: editorPluginAIProvider.rangeConfig,
            aiGlobalOptIn: aiOptIn
          })
        });
      }
      if (isFullPageExperimentsEnabled && getBooleanFF('platform.editor.ai-suggest-title-in-title-toolbar_h3ww0')) {
        createAISuggestTitleFromTitleToolbarPlugin;
        plugins.push({
          name: 'ai-suggest-title-from-title-toolbar',
          plugin: _options => createAISuggestTitleFromTitleToolbarPlugin({
            aiGlobalOptIn: aiOptIn
          })
        });
      }
      if (isAIPanelsEnabled()) {
        plugins.push({
          name: 'ai-panels',
          plugin: () => createAIPanelsPlugin()
        });
      }
      const persistentPlaceholderCohort = getExperimentCohort('editor_ai_-_persistent_placeholder_experiment');
      const isPersistentPlaceholderEnabled = isExperimentEnabled('editor_ai_-_persistent_placeholder_experiment');
      if (isFullPageExperimentsEnabled && persistentPlaceholderCohort === 'persistent-placeholder' && isPersistentPlaceholderEnabled && aiOptIn.status === 'enabled') {
        plugins.push({
          name: 'ai-patch-editor-placeholder-plugin',
          plugin: options => createAIPlaceholderPlugin({
            getIntl: options.getIntl,
            api
          })
        });
      }
      return plugins;
    },
    actions: {
      triggerAIPanelFeedbackCollector: (extensionKey, markdown, locale, fireAnalyticsEvent) => {
        return triggerAIPanelFeedbackCollector({
          extensionKey,
          markdown,
          locale,
          editorPluginAIProvider,
          fireAnalyticsEvent
        });
      },
      getInitialAIPanelContext: ({
        intl,
        configItem,
        intentSchemaId
      }) => {
        return getAIPanelContext({
          configItem,
          intentSchemaId,
          editorView,
          providerFactory,
          idMap: newIDMap,
          intl,
          updateIdMap
        });
      },
      summariseDocument: (callback, intl, initialContext) => {
        summarisePage({
          editorView,
          generativeAIApiUrl: editorPluginAIProvider.generativeAIApiUrl,
          product: editorPluginAIProvider.product,
          callback,
          intl,
          initialContext
        });
      },
      findActionItems: (callback, intl, initialContext) => {
        findActionItems({
          editorView,
          generativeAIApiUrl: editorPluginAIProvider.generativeAIApiUrl,
          product: editorPluginAIProvider.product,
          callback,
          intl,
          initialContext
        });
      },
      convertExtension: (localId, extensionKey, addToHistory) => {
        convertExtension({
          editorView,
          localId,
          extensionKey,
          idMap: newIDMap,
          addToHistory
        });
      },
      regenerateExtension: (localId, extensionKey) => {
        regenerateExtension({
          editorView,
          localId,
          extensionKey,
          idMap: newIDMap
        });
      }
    },
    pluginsOptions: {
      selectionToolbar: (state, intl) => {
        const items = [];
        if (editorPluginAIProvider.disableAISelectionToolbar) {
          return;
        }
        const aiDropdowns = getAIExperienceDropdowns({
          intl,
          rangeConfig: editorPluginAIProvider.rangeConfig,
          aiGlobalOptIn: aiOptIn
        });
        const aiIconButton = getAIExperienceButton({
          intl,
          rangeBaseGenerate: editorPluginAIProvider.rangeConfig.baseGenerate,
          hideTitle: !!aiDropdowns.length,
          aiGlobalOptIn: aiOptIn
        });
        if (getBooleanFF('platform.editor.enable-selection-toolbar_ucdwd')) {
          items.push(aiIconButton, ...aiDropdowns);
        }
        return {
          // make sure the AI button is after the comment button in Confluence
          rank: -10,
          isToolbarAbove: true,
          items
        };
      },
      quickInsert: quickInsert,
      floatingToolbar: getFloatingToolbarConfig()
    },
    contentComponent: ({
      editorView,
      wrapperElement,
      appearance
    }) => {
      // We use this point to render the modal if needed instead of decorators
      // The reason this is preferrable is it has access to mount points
      // where decorators are rendered with a mount point within the Editor content
      // and longer modals get clipped as a result.
      const renderModalRegion = aiExperienceDecorationPluginState => {
        // If there are no active decorations -- then we don't want to render the ai experience.
        if (!aiExperienceDecorationPluginState || aiExperienceDecorationPluginState.modalDecorationSet === DecorationSet.empty) {
          return null;
        }
        if (hasGeneratedContentDecorations(aiExperienceDecorationPluginState)) {
          return null;
        }
        const modalDecorationElement = editorView === null || editorView === void 0 ? void 0 : editorView.dom.querySelector('.ai-modal-end-widget');

        // This protects against cases where there is an active decoration
        // but the document has not been updated to include it
        if (!modalDecorationElement) {
          return null;
        }

        // Prioritise getting position from inlineDecoration,
        // otherwise get decoration position from start or end widget
        const decoration = aiExperienceDecorationPluginState.modalDecorationSet.find(undefined, undefined, spec => spec.key === 'inlineDecoration').shift() || aiExperienceDecorationPluginState.modalDecorationSet.find(undefined, undefined, spec => spec.key === 'startWidgetDecoration' || spec.key === 'endWidgetDecoration').shift();
        if (!decoration) {
          return null;
        }
        const positions = [decoration.from, decoration.to];
        const endExperience = () => {
          // TODO POSTEAP
          // When the full experience is moved to the state machine -- the intention is
          // to have the modal experience ended via state transitions like "action taken"
          // or "modal dismissed" and have side effects (actions) which run as part of
          // those transition to do any clean up.
          // Also to note -- this is not currently the end of the AI experience
          // following this being called -- we decorate the document with a highlight following
          // actions taken.

          const state = getPluginState(editorView.state);
          const endAIExperienceCommand = createEndAIExperienceCommand(hasGeneratedContentDecorations(state) ? state.modalDecorationSet : undefined);
          endAIExperienceCommand(editorView.state, editorView.dispatch);
          editorView.focus();
        };
        return (
          /*#__PURE__*/
          // TODO POSTEAP
          // This error boundary will be unexpectedly positioned
          // as it's not wrapped in the ModalRegion.
          // In the case the modal region throws an exception
          // we should come up with an alternative positioning
          // strategy for error feedback.
          React.createElement(ModalRegionErrorBoundary, {
            onCloseFallback: endExperience
          }, /*#__PURE__*/React.createElement(ModalRegion, {
            key: aiExperienceDecorationPluginState.modalMountedTimeStamp,
            decoration: decoration,
            modalDecorationElement: modalDecorationElement,
            editorView: editorView,
            editorRelativeWrapper: wrapperElement === null || wrapperElement === void 0 ? void 0 : wrapperElement.offsetParent,
            endExperience: endExperience,
            appearance: appearance
          }, aiExperienceDecorationPluginState.configItem && editorView && /*#__PURE__*/React.createElement(ExperienceApplication
          // WIP wiring
          , {
            configItem: aiExperienceDecorationPluginState.configItem,
            editorPluginAIProvider: editorPluginAIProvider,
            endExperience: endExperience,
            positions: positions,
            editorView: editorView,
            providerFactory: providerFactory
            // TODO POSTEAP
            // This prop is being used for analytics but is temporary.
            // Plugin re-architecture will change this.
            // https://product-fabric.atlassian.net/wiki/spaces/EUXQ/pages/3573548013/Introducing+AI+Dev+Documentation+TODO+AIFOLLOWUP+audit#Plugin-re-architecture
            ,
            lastTriggeredFrom: aiExperienceDecorationPluginState.lastTriggeredFrom,
            triggerMethod: aiExperienceDecorationPluginState.triggerMethod,
            appearance: appearance
          })))
        );
      };
      let pluginsToTrack = {
        aiExperienceDecorationPluginState: aiExperienceDecorationPluginKey
      };
      const persistentUICohort = getExperimentCohort('editor_ai_-_persistent_button_experiment');
      const persistentUIEnabled = isExperimentEnabled('editor_ai_-_persistent_button_experiment');
      if (persistentUICohort === 'floating-button' && persistentUIEnabled) {
        // listen on positon change to rerender floating ai button
        pluginsToTrack.floatingAiButtonTrackerState = floatingAiButtonTrackerPluginKey;
      }
      if (persistentUICohort === 'tip-tap-menu' && persistentUIEnabled) {
        const typeAheadPluginKeyName = 'typeAheadPlugin$';
        const typeAheadPluginKey = {
          key: typeAheadPluginKeyName,
          getState: state => {
            return state[typeAheadPluginKeyName];
          },
          get: state => {
            return state[typeAheadPluginKeyName];
          }
        };
        const placeholderPluginKeyName = 'placeholderPlugin$';
        const placeholderPluginKey = {
          key: placeholderPluginKeyName,
          getState: state => {
            return state[placeholderPluginKeyName];
          },
          get: state => {
            return state[placeholderPluginKeyName];
          }
        };
        //listen on positon change to rerender floating ai button
        pluginsToTrack.floatingAiButtonTrackerState = floatingAiButtonTrackerPluginKey;
        // for tip tap mode listen on typeahead state change to hide button when typeahead is open
        pluginsToTrack.typeAheadPluginState = typeAheadPluginKey;
        // for tip tap mode listen to placeholder state change to hide button when placeholder is present
        pluginsToTrack.placeholderPluginState = placeholderPluginKey;
      }
      return /*#__PURE__*/React.createElement(GlobalWrapper, null, /*#__PURE__*/React.createElement(WithPluginState, {
        plugins: pluginsToTrack,
        render: ({
          aiExperienceDecorationPluginState,
          floatingAiButtonTrackerState
        }) => {
          // The WithPluginState function does not support typing the plugin states it returns
          // so we need to manually assert this.
          const modalRegion = renderModalRegion(aiExperienceDecorationPluginState);
          const selection = editorView.state.selection;
          if (modalRegion) {
            return modalRegion;
          }
          const showFloatingAIButton = persistentUICohort === 'floating-button';
          const showTipTapMenu = persistentUICohort === 'tip-tap-menu';
          const showPersistentButtonExperiment = floatingAiButtonTrackerState && selection instanceof TextSelection && (showFloatingAIButton || showTipTapMenu) && persistentUIEnabled;
          if (!showPersistentButtonExperiment) {
            return null;
          }
          return /*#__PURE__*/React.createElement(AskAIButtonWrapper, {
            editorView: editorView,
            selection: selection,
            editorPluginAIProvider: editorPluginAIProvider,
            showButton: ['full-page', 'full-width'].includes(appearance) && floatingAiButtonTrackerState.shouldShowAskAIButton,
            aiGlobalOptIn: aiOptIn,
            appearance: persistentUICohort
          });
        }
      }));
    },
    primaryToolbarComponent: ({
      editorView
    }) => {
      return /*#__PURE__*/React.createElement(WithPluginState, {
        plugins: {
          pluginState: aiButtonPluginKey,
          spellingAndGrammar: aiSpellingGrammarPluginKey
        },
        render: ({
          pluginState,
          spellingAndGrammar
        }) => {
          const aiButtonPluginState = pluginState;
          const spellingAndGrammarPluginState = spellingAndGrammar;
          const onClick = () => {
            const {
              state,
              dispatch
            } = editorView;
            const configItem = state.selection.empty ? editorPluginAIProvider.emptyConfig.baseGenerate : editorPluginAIProvider.rangeConfig.baseGenerate;
            const openAIModalCommand = createOpenAIModalCommand({
              state,
              configItem,
              lastTriggeredFrom: 'mainToolbar',
              aiGlobalOptIn: aiOptIn
            });
            openAIModalCommand(state, dispatch);
          };
          const realtimeAIOnClick = async () => {
            const {
              state,
              dispatch
            } = editorView;
            toggleSpellingAndGrammar()(state, dispatch);
          };
          const Icon = () => /*#__PURE__*/React.createElement("svg", {
            width: "24",
            height: "24",
            viewBox: "0 0 24 24",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg"
          }, /*#__PURE__*/React.createElement("path", {
            d: "M12 4C12.676 4 13.284 4.399 13.556 5.008L13.609 5.142L16.944 14.67C17.0283 14.9108 17.0173 15.1748 16.9134 15.4078C16.8094 15.6408 16.6203 15.8252 16.3848 15.9234C16.1493 16.0215 15.8851 16.0259 15.6465 15.9357C15.4078 15.8455 15.2127 15.6674 15.101 15.438L15.056 15.33L14.232 12.973L14.117 12.993L14 13H10C9.92255 12.9999 9.84536 12.9909 9.77 12.973L8.944 15.33C8.86313 15.5614 8.70011 15.755 8.4859 15.8741C8.27169 15.9932 8.0212 16.0294 7.782 15.976L7.67 15.944C7.43863 15.8631 7.24502 15.7001 7.12593 15.4859C7.00684 15.2717 6.97057 15.0212 7.024 14.782L7.056 14.67L10.391 5.142C10.5078 4.80818 10.7255 4.51893 11.0139 4.31432C11.3024 4.10971 11.6473 3.99987 12.001 4H12ZM12 6.598L10.46 11H13.542L12.002 6.598H12Z",
            fill: "currentColor"
          }), /*#__PURE__*/React.createElement("path", {
            d: "M14 17L16 19L20 15",
            stroke: "currentColor",
            strokeWidth: "2",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }));
          return /*#__PURE__*/React.createElement(React.Fragment, null, isProactiveAISupported && /*#__PURE__*/React.createElement(IconButton, {
            isSelected: spellingAndGrammarPluginState.isSpellingAndGrammarEnabled,
            icon: Icon,
            appearance: "subtle",
            onClick: realtimeAIOnClick,
            label: "Spellcheck"
          }), /*#__PURE__*/React.createElement(AtlassianIntelligenceToolbarButton, {
            ButtonWrapper: editorPluginAIProvider === null || editorPluginAIProvider === void 0 ? void 0 : editorPluginAIProvider.AIButtonWrapper,
            onClick: onClick,
            disabled: !(aiButtonPluginState !== null && aiButtonPluginState !== void 0 && aiButtonPluginState.isSelectionValidForAIExperience)
          }));
        }
      });
    }
  };
};